import { useRequiredContext } from "@redotech/react-util/context";
import type { CoverageQaSettings } from "@redotech/redo-coverage-common/qa";
import { toast } from "@redotech/redo-web/alert";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Text } from "@redotech/redo-web/text";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { memo } from "react";
import { RedoAdminRpcClientContext } from "../../../../app/redo-admin-rpc-client-provider";

interface CoverageQaCardProps {
  teamId: string;
}

export const CoverageQaCard = memo(function CoverageQaCard({
  teamId,
}: CoverageQaCardProps) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const queryClient = useQueryClient();
  const queryKey = ["coverageQa", teamId];
  const { data, isPending, error } = useQuery({
    queryKey,
    queryFn: () => rpcClient.getCoverageQa({ teamId }),
  });
  const save = useMutation({
    mutationFn: (settings: CoverageQaSettings) =>
      rpcClient.updateCoverageQaSettings({ teamId, ...settings }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
      toast("Coverage QAgent settings saved", { variant: "success" });
    },
    onError: (error) =>
      toast(error instanceof Error ? error.message : String(error), {
        variant: "error",
      }),
  });
  if (isPending) {
    return (
      <Text fontSize="sm" textColor="secondary">
        Loading Coverage QAgent settings...
      </Text>
    );
  }
  if (error || !data) {
    return (
      <Text fontSize="sm" textColor="error">
        Coverage QAgent settings could not load.
      </Text>
    );
  }
  return (
    <RedoToggle
      description="Allow QAgent to inspect this merchant's Coverage when its live Shopify theme changes."
      disabled={save.isPending}
      label="Enable Coverage QAgent"
      layout="trailing"
      settingsStyle
      setValue={(enabled) => save.mutate({ enabled })}
      value={data.settings.enabled}
    />
  );
});
